/**
 * API Keys Management Page
 * Allows users to create, view, and revoke API keys for third-party integrations
 */

import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  is_active: boolean;
  last_used_at: string | null;
  usage_count: number;
  created_at: string;
  revoked_at: string | null;
}

interface NewApiKey extends ApiKey {
  key: string;
}

export default function ApiKeys() {
  const { tenant } = useAuth();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState<NewApiKey | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [featureAvailable, setFeatureAvailable] = useState<boolean | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string>('');

  const checkFeatureAvailability = async () => {
    try {
      const response = await api.billing.getFeatures();
      const data = response.data;
      setFeatureAvailable(data.features?.api_access || false);
      setCurrentPlan(data.plan || 'starter');
    } catch (error) {
      console.error('Failed to check feature availability:', error);
      setFeatureAvailable(false);
    }
  };

  const loadApiKeys = async () => {
    if (!tenant?.id) return;
    
    try {
      setLoading(true);
      const response = await api.apiKeys.list(tenant.id);
      const data = response.data;
      // Ensure we always set an array
      setApiKeys(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Failed to load API keys:', error);
      // If error is 403, it means feature is not available
      if (error.response?.status === 403) {
        setFeatureAvailable(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenant) {
      checkFeatureAvailability();
      if (featureAvailable !== false) {
        loadApiKeys();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  // Show upgrade prompt if feature is not available
  if (featureAvailable === false) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">API Access Not Available</h2>
          <p className="text-gray-600 mb-6">
            API access is only available on the <strong>Growth</strong> plan or higher.
            Your current plan (<strong className="capitalize">{currentPlan}</strong>) does not include this feature.
          </p>
          <div className="flex gap-4 justify-center">
            <a
              href="/billing"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Upgrade to Growth Plan
            </a>
            <button
              onClick={() => window.history.back()}
              className="inline-block px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      alert('Please enter a name for the API key');
      return;
    }

    if (!tenant?.id) {
      alert('No tenant selected');
      return;
    }

    try {
      setCreating(true);
      const response = await api.apiKeys.create({
        name: newKeyName,
        tenant_id: tenant.id,
      });
      
      setNewKeyData(response.data);
      setShowNewKeyModal(true);
      setNewKeyName('');
      await loadApiKeys();
    } catch (error: any) {
      console.error('Failed to create API key:', error);
      alert(error.response?.data?.detail || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeKey = async (keyId: string, keyName: string) => {
    if (!confirm(`Are you sure you want to revoke the API key "${keyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.apiKeys.revoke(keyId);
      await loadApiKeys();
    } catch (error: any) {
      console.error('Failed to revoke API key:', error);
      alert(error.response?.data?.detail || 'Failed to revoke API key');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('API key copied to clipboard!');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return <div className="p-6">Loading API keys...</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">API Keys</h1>
        <p className="text-gray-600">
          Manage API keys for third-party integrations like Zapier, webhooks, and custom applications.
        </p>
      </div>

      {/* Create New Key Section */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Create New API Key</h2>
        <div className="flex gap-4">
          <input
            type="text"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder="Enter API key name (e.g., 'Zapier Integration')"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={handleCreateKey}
            disabled={creating || !newKeyName.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {creating ? 'Creating...' : 'Create API Key'}
          </button>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          ⚠️ The API key will only be shown once upon creation. Make sure to save it securely.
        </p>
      </div>

      {/* API Keys List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Your API Keys</h2>
        </div>

        {apiKeys.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No API keys yet. Create one above to get started with integrations.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Key</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Used</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {apiKeys.map((key) => (
                  <tr key={key.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{key.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                        {key.key_prefix}••••••••••••
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {key.revoked_at ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          Revoked
                        </span>
                      ) : key.is_active ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(key.last_used_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {key.usage_count.toLocaleString()} calls
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(key.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {!key.revoked_at && (
                        <button
                          onClick={() => handleRevokeKey(key.id, key.name)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New API Key Modal */}
      {showNewKeyModal && newKeyData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full">
            <h2 className="text-2xl font-bold mb-4">🎉 API Key Created Successfully!</h2>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <strong>Important:</strong> This is the only time you'll see the full API key. 
                    Copy it now and store it securely. You won't be able to retrieve it later.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">API Key Name</label>
              <div className="bg-gray-50 px-4 py-3 rounded-md">
                <p className="text-gray-900 font-medium">{newKeyData.name}</p>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newKeyData.key}
                  readOnly
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-md bg-gray-50 font-mono text-sm"
                />
                <button
                  onClick={() => copyToClipboard(newKeyData.key)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Copy
                </button>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold mb-2">How to use this API key:</h3>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                <li>Copy the API key above</li>
                <li>Go to Zapier or your integration platform</li>
                <li>When connecting to ReputationManager, paste this key</li>
                <li>Select your tenant/workspace</li>
                <li>Start building automations!</li>
              </ol>
            </div>

            <button
              onClick={() => setShowNewKeyModal(false)}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              I've Saved My API Key
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

